import { useState, useMemo } from 'react'
import { Loader2 } from 'lucide-react'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { Label } from '@/components/ui/label'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Slider } from '@/components/ui/slider'
import { usePartyDistrictStatesWithDistricts } from '@/hooks/usePartyDistrictStatesWithDistricts'
import { useCreateAction } from '@/hooks/useCreateAction'
import {
  estimateCampaignEffect,
  getEfficiencyColor,
  formatEfficiency,
} from '@/lib/actionEffects'
import { cn } from '@/lib/utils'

interface CampaignActionFormProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  partyId: string
  currentTick: number
  availableAP: number
  onSuccess?: () => void
  onBack?: () => void
}

type TargetType = 'national' | 'district'

export function CampaignActionForm({
  open,
  onOpenChange,
  partyId,
  currentTick,
  availableAP,
  onSuccess,
  onBack,
}: CampaignActionFormProps) {
  if (!open) return null

  return (
    <CampaignActionFormContent
      open={open}
      onOpenChange={onOpenChange}
      partyId={partyId}
      currentTick={currentTick}
      availableAP={availableAP}
      onSuccess={onSuccess}
      onBack={onBack}
    />
  )
}

function CampaignActionFormContent({
  open,
  onOpenChange,
  partyId,
  currentTick,
  availableAP,
  onSuccess,
  onBack,
}: CampaignActionFormProps) {
  // Form state
  const [targetType, setTargetType] = useState<TargetType>('district')
  const [selectedDistrictId, setSelectedDistrictId] = useState<string>('')
  const [intensity, setIntensity] = useState(1)

  // Fetch party's district states with district details
  const { districtStates, isLoading: districtsLoading } =
    usePartyDistrictStatesWithDistricts(partyId)

  // Create action mutation
  const { createAction, isCreating, error } = useCreateAction({
    onSuccess: () => {
      onSuccess?.()
      onOpenChange(false)
    },
  })

  // Get selected district info
  const selectedDistrictState = useMemo(() => {
    if (targetType === 'national' || !selectedDistrictId) return null
    return districtStates?.find((ds) => ds.district_id === selectedDistrictId)
  }, [targetType, selectedDistrictId, districtStates])

  // Calculate current recognition for effect estimate
  const currentRecognition = useMemo(() => {
    if (targetType === 'national') {
      // For national, use average recognition across all districts
      if (!districtStates || districtStates.length === 0) return 0
      const totalRecog = districtStates.reduce(
        (sum, ds) => sum + (ds.recognition ?? 0),
        0
      )
      return Math.round(totalRecog / districtStates.length)
    }
    return selectedDistrictState?.recognition ?? 0
  }, [targetType, districtStates, selectedDistrictState])

  // Calculate estimated effects
  const effectEstimate = useMemo(() => {
    return estimateCampaignEffect(
      intensity,
      currentRecognition,
      targetType === 'national',
      selectedDistrictState?.district?.population
    )
  }, [intensity, currentRecognition, targetType, selectedDistrictState])

  // Validation
  const canSubmit =
    !isCreating &&
    intensity <= availableAP &&
    (targetType === 'national' || selectedDistrictId)

  const apAfterAction = availableAP - intensity

  const handleSubmit = () => {
    if (!canSubmit) return

    createAction({
      party_id: partyId,
      tick: currentTick,
      action_type: 'campaign',
      action_subtype:
        targetType === 'national' ? 'national_campaign' : 'district_campaign',
      target_type: targetType,
      target_id: targetType === 'district' ? selectedDistrictId : undefined,
      action_points_spent: intensity,
      parameters: {
        intensity,
        target_district_name: selectedDistrictState?.district?.name,
      },
    })
  }

  const handleBack = () => {
    onBack?.()
    onOpenChange(false)
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <span>🎯</span> Configure Campaign
          </DialogTitle>
          <DialogDescription>
            Run ads, rallies, and canvassing to increase recognition and win
            voter support.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-6 py-4">
          {/* Target Type Selection */}
          <div className="space-y-2">
            <Label>Target</Label>
            <div className="grid grid-cols-2 gap-2">
              <Button
                type="button"
                variant={targetType === 'national' ? 'default' : 'outline'}
                className="w-full"
                onClick={() => setTargetType('national')}
              >
                National
              </Button>
              <Button
                type="button"
                variant={targetType === 'district' ? 'default' : 'outline'}
                className="w-full"
                onClick={() => setTargetType('district')}
              >
                District
              </Button>
            </div>
            <p className="text-muted-foreground text-xs">
              {targetType === 'national'
                ? 'Broad campaign affecting all districts (less efficient but wider reach)'
                : 'Focused campaign on a single district (more efficient, localized effect)'}
            </p>
          </div>

          {/* District Selector (only shown for district targeting) */}
          {targetType === 'district' && (
            <div className="space-y-2">
              <Label htmlFor="district-select">Select District</Label>
              {districtsLoading ? (
                <div className="flex h-9 items-center justify-center">
                  <Loader2 className="text-muted-foreground h-4 w-4 animate-spin" />
                </div>
              ) : (
                <>
                  <Select
                    value={selectedDistrictId}
                    onValueChange={setSelectedDistrictId}
                  >
                    <SelectTrigger id="district-select">
                      <SelectValue placeholder="Choose a district..." />
                    </SelectTrigger>
                    <SelectContent>
                      {districtStates?.map((ds) => (
                        <SelectItem key={ds.district_id} value={ds.district_id}>
                          {ds.district.name} ({ds.district.region.name})
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  {selectedDistrictState && (
                    <p className="text-muted-foreground text-xs">
                      Current recognition: {selectedDistrictState.recognition}%
                      | Population:{' '}
                      {selectedDistrictState.district.population.toLocaleString()}
                    </p>
                  )}
                </>
              )}
            </div>
          )}

          {/* National average recognition display */}
          {targetType === 'national' && (
            <p className="text-muted-foreground text-xs">
              Average recognition across all districts: {currentRecognition}%
            </p>
          )}

          {/* Intensity Slider */}
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <Label>Intensity</Label>
              <span className="text-sm font-medium">{intensity} AP</span>
            </div>
            <Slider
              value={[intensity]}
              onValueChange={([value]) => setIntensity(value)}
              min={1}
              max={Math.min(5, availableAP)}
              step={1}
              disabled={availableAP < 1}
            />
            <div className="text-muted-foreground flex justify-between text-xs">
              <span>1 - Minimal</span>
              <span>3 - Significant</span>
              <span>5 - Full Blitz</span>
            </div>
          </div>

          {/* Effect Preview */}
          <div className="bg-muted/50 space-y-2 rounded-lg border p-4">
            <h4 className="text-sm font-medium">Estimated Effects</h4>
            <div className="space-y-1 text-sm">
              <div className="flex justify-between">
                <span className="text-muted-foreground">Recognition boost:</span>
                <span className="font-medium">
                  +{effectEstimate.recognitionBoost}% ({currentRecognition}% →{' '}
                  {Math.min(100, currentRecognition + effectEstimate.recognitionBoost)}%)
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-muted-foreground">Voters influenced:</span>
                <span className="font-medium">
                  ~{effectEstimate.votersInfluenced.toLocaleString()}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-muted-foreground">Efficiency:</span>
                <span
                  className={cn('font-medium', getEfficiencyColor(effectEstimate.efficiency))}
                >
                  {formatEfficiency(effectEstimate.efficiency)}
                </span>
              </div>
              <p className="text-muted-foreground pt-1 text-xs">
                {effectEstimate.efficiencyReason}
              </p>
            </div>
          </div>

          {/* AP Summary */}
          <div className="flex items-center justify-between text-sm">
            <span className="text-muted-foreground">AP after action:</span>
            <span className={cn('font-medium', apAfterAction < 0 && 'text-destructive')}>
              {availableAP} → {Math.max(0, apAfterAction)}
            </span>
          </div>

          {/* Error display */}
          {error && (
            <p className="text-destructive text-sm">
              {error.message || 'Failed to queue action'}
            </p>
          )}
        </div>

        <DialogFooter>
          <Button type="button" variant="outline" onClick={handleBack} disabled={isCreating}>
            Back
          </Button>
          <Button type="button" onClick={handleSubmit} disabled={!canSubmit}>
            {isCreating && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            {isCreating ? 'Queuing...' : 'Queue Action'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
